import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isEmpty, isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import {
  getCityVillages,
  selectSortedCityVillages,
  selectSortedCountries,
  selectSortedRAStates,
} from '../../../../../redux/modules/countries';
import { APPLICANT_RESIDENCE_ADDRESS_FIELDS } from '../../../options';
import ArmenianRegionSelectOptions from '../../../../../components/FormComponents/ContactInfo/ArmenianRegionSelectOptions';
import TextInputFieldWithValidation from '../../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import SelectWithValidation from '../../../../../components/ValidationFormElements/SelectWithValidation';
import useRegisterValidationFields from '../../../../../hooks/useRegisterValidationFields';
import { findIsYerevan, isArmenianCountry } from '../../../../../helpers/countryHelper';
import usePrevious from '../../../../../hooks/usePrevious';
import { APPLICANT_TO_VICTIM } from '../../../../../constants';
import { getMenuLabelWithTranslation } from '../../../../../helpers/selectHelpers';
import ResidenceIcon from '../../../../../img/formSectionIcons/applicantResidenceAddress.svg';

import FormSection from '../../../../../components/FormComponents/FormSection';

const ApplicantResidenceAddress = ({
  formProps,
  initialData,
  applicantIs,
  previousApplicantState,
}) => {
  const { i18n } = useTranslation();

  const { register, setValue, errors, clearError, triggerValidation } = formProps;
  const dispatch = useDispatch();

  const { cityVillagesLoading } = useSelector((store) => store.countriesReducer);
  const countries = useSelector((state) => selectSortedCountries(state, i18n.language));
  const states = useSelector((state) => selectSortedRAStates(state, i18n.language));
  const cityVillages = useSelector((state) => selectSortedCityVillages(state, i18n.language));
  const previousApplicantIs = usePrevious(applicantIs);

  const [isArmenianAddress, setIsArmenianAddress] = useState(false);
  const [applicantResidenceCountry, setApplicantResidenceCountry] = useState(null);
  const [applicantResidenceState, setApplicantResidenceState] = useState(null);
  const [applicantResidenceCityVillage, setApplicantResidenceCityVillage] = useState(null);
  const [applicantResidenceStreet, setApplicantResidenceStreet] = useState('');

  useRegisterValidationFields(APPLICANT_RESIDENCE_ADDRESS_FIELDS, register);

  // Here {applicantIs} used to catch each change related only applicant checkboxes
  useEffect(() => {
    setApplicantResidenceStreet(initialData.applicantResidenceStreet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIs]);

  const isYerevan = useMemo(() => findIsYerevan(applicantResidenceState), [
    applicantResidenceState,
  ]);

  const onCountryChange = useCallback(
    (value) => {
      setApplicantResidenceCountry(value);
      setIsArmenianAddress(isArmenianCountry(value));
      setValue('applicantResidenceCountry', value, true);
      if (!isArmenianCountry(value)) {
        setValue('applicantResidenceCityVillage', null);
        setValue('applicantResidenceState', null);
        setApplicantResidenceState(null);
        setApplicantResidenceCityVillage(null);
      }
      if (applicantResidenceStreet) {
        triggerValidation({ name: 'applicantResidenceStreet' });
      }
    },
    [setValue, triggerValidation, applicantResidenceStreet],
  );

  const onStateChange = useCallback(
    (value) => {
      setValue('applicantResidenceState', value, true);
      setApplicantResidenceState(value);
      dispatch(getCityVillages(value.id));
      setApplicantResidenceCityVillage(null);
      setValue('applicantResidenceCityVillage', null);
    },
    [dispatch, setValue],
  );

  const onCityVillageChange = useCallback(
    (value) => {
      setValue('applicantResidenceCityVillage', value, true);
      setApplicantResidenceCityVillage(value);
    },
    [setValue],
  );

  useEffect(() => {
    if (initialData.applicantResidenceState && applicantIs !== previousApplicantIs) {
      // eslint-disable-next-line array-callback-return,consistent-return
      const state = states.find((item) => {
        if (
          item.nameHY === initialData.applicantResidenceState
          || item.nameHY === initialData.applicantResidenceState.nameHY
        ) {
          return item;
        }
      });
      if (state) {
        onStateChange(state);
      }
    }
  }, [
    initialData.applicantResidenceState,
    applicantIs,
    previousApplicantIs,
    states,
    onStateChange,
  ]);

  useEffect(() => {
    if (initialData.applicantResidenceCityVillage) {
      // eslint-disable-next-line array-callback-return,consistent-return
      const cityVillage = cityVillages.find((item) => {
        if (
          item.nameHY === initialData.applicantResidenceCityVillage
          || item.nameHY === initialData.applicantResidenceCityVillage.nameHY
        ) {
          return item;
        }
      });
      if (cityVillage) {
        onCityVillageChange(cityVillage);
      }
    }
  }, [initialData.applicantResidenceCityVillage, cityVillages, onCityVillageChange]);

  useEffect(() => {
    if (initialData.applicantResidenceCountry && applicantIs !== previousApplicantIs) {
      const country = countries.find(
        // eslint-disable-next-line array-callback-return,consistent-return
        (item) => {
          if (
            // @TODO check why {applicantResidenceCountry} type has been changed
            item.nameHY === initialData.applicantResidenceCountry.nameHY
            || item.nameHY === initialData.applicantResidenceCountry
          ) {
            return item;
          }
        },
      );
      if (country) {
        onCountryChange(country);
        setIsArmenianAddress(isArmenianCountry(country));
      }
    }
  }, [
    initialData.applicantResidenceCountry,
    applicantIs,
    previousApplicantIs,
    countries,
    onCountryChange,
  ]);

  // Here {applicantIs} used to catch each change related only applicant checkboxes
  useEffect(() => {
    setValue('applicantResidenceStreet', applicantResidenceStreet, true);
  }, [applicantResidenceStreet, setValue, applicantIs]);

  useEffect(() => {
    if (cityVillages && cityVillages.length === 1) {
      onCityVillageChange(cityVillages[0]);
    }
  }, [cityVillages, onCityVillageChange]);

  useEffect(() => {
    if (
      !isUndefined(previousApplicantState)
      && applicantIs !== previousApplicantState
      && previousApplicantState !== APPLICANT_TO_VICTIM[1].value
    ) {
      setValue('applicantResidenceCountry', null);
      setValue('applicantResidenceState', null);
      setValue('applicantResidenceCityVillage', null);
      setValue('applicantResidenceStreet', '');

      setApplicantResidenceCountry(null);
      setApplicantResidenceState(null);
      setApplicantResidenceCityVillage(null);
      setApplicantResidenceStreet('');
    }
  }, [applicantIs, previousApplicantState, setValue]);

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n.language),
    [i18n],
  );

  return (
    <FormSection title="claim.personalData.applicantResidenceAddress" icon={ResidenceIcon}>
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
          md={!isEmpty(applicantResidenceState) && isArmenianAddress && !isYerevan ? 4 : 6}
        >
          <SelectWithValidation
            formProps={{ errors, clearError }}
            value={applicantResidenceCountry || ''}
            options={countries}
            getMenuLabel={getLabelWithTranslation}
            onChange={onCountryChange}
            label="contactInfo.country"
            name="applicantResidenceCountry"
            required
          />
        </Grid>
        {isArmenianAddress && (
          <>
            <ArmenianRegionSelectOptions
              cityVillages={cityVillages}
              onCityVillageChange={onCityVillageChange}
              cityVillagesLoading={cityVillagesLoading}
              errors={errors}
              clearError={clearError}
              states={states}
              isYerevan={isYerevan}
              form={{
                cityVillage: {
                  value: applicantResidenceCityVillage,
                  label: 'contactInfo.cityVillage',
                  name: 'applicantResidenceCityVillage',
                },
                state: {
                  value: applicantResidenceState,
                  label: 'contactInfo.state',
                  name: 'applicantResidenceState',
                },
              }}
              onStateChange={onStateChange}
              required
            />
          </>
        )}
        <Grid item xs={12}>
          <TextInputFieldWithValidation
            formProps={{ errors, clearError }}
            labelKey="contactInfo.address"
            name="applicantResidenceStreet"
            handleChange={(e) => {
              setApplicantResidenceStreet(e.target.value);
              setValue('applicantResidenceStreet', e.target.value, true);
            }}
            value={applicantResidenceStreet}
            required
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

ApplicantResidenceAddress.propTypes = {
  applicantIs: PropTypes.string.isRequired,
  initialData: PropTypes.shape({
    applicantResidenceCountry: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    applicantResidenceState: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    applicantResidenceCityVillage: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    applicantResidenceStreet: PropTypes.string,
  }).isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    triggerValidation: PropTypes.func,
  }).isRequired,
  previousApplicantState: PropTypes.string,
};

ApplicantResidenceAddress.defaultProps = {
  previousApplicantState: undefined,
};

export default ApplicantResidenceAddress;
